import React, { useEffect, useRef, useState } from 'react';
import * as SC from '@components/common/checkbox/CheckBox.style';
import CheckBoxIcon from '@components/common/checkbox/CheckBoxIcon';

export type CheckBoxContextType = {
  targetValue: string;
  isChecked: boolean;
  disabled: boolean;
  checked?: boolean;
  divRef?: React.RefObject<HTMLDivElement>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCheck: () => void;
};

const CheckBoxContext = React.createContext<CheckBoxContextType | null>(null);

export const useCheckBox = () => {
  const context = React.useContext(CheckBoxContext);
  if (!context) {
    throw new Error('useCheckBox cannot be called outside the Checkbox');
  }

  return context;
};

export type CheckBoxProps = {
  children: React.ReactNode;
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  targetValue: string;
  disabled?: boolean;
  divRef?: React.RefObject<HTMLDivElement>;
};

const CheckBox = ({ children, checked, disabled = false, onChange, targetValue, divRef }: CheckBoxProps) => {
  const [isChecked, setIsChecked] = useState(checked ?? false);

  useEffect(() => {
    if (isChecked !== checked) {
      setIsChecked(checked ?? false);
    }
  }, [isChecked, checked]);

  const onCheck = React.useCallback(() => setIsChecked((prev) => !prev), []);

  const value = React.useMemo(
    () => ({
      checked,
      onChange,
      isChecked,
      disabled,
      onCheck,
      targetValue,
      divRef,
    }),
    [checked, onChange, targetValue, isChecked, onCheck, disabled, divRef],
  );

  return <CheckBoxContext.Provider value={value}>{children}</CheckBoxContext.Provider>;
};

type IndicatorProps = {
  children: React.ReactNode;
  readOnly?: boolean;
};

const Indicator = ({ children, readOnly }: IndicatorProps) => {
  const { checked, onChange, onCheck, targetValue, disabled, divRef } = useCheckBox();

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <SC.Flex ref={divRef} onClick={() => inputRef?.current?.click()}>
        {children}
      </SC.Flex>
      <SC.InputWrapper
        type='checkbox'
        id={targetValue}
        ref={inputRef}
        checked={checked}
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onCheck();
          onChange && onChange(e);
        }}
        readOnly={readOnly}
      />
    </>
  );
};

type LabelProps = {
  children: React.ReactNode;
  className?: string;
};

export type CheckBoxChildrenProps = {
  isChecked: boolean;
  isDisabled: boolean;
};

const Icon = () => {
  const { isChecked, disabled } = useCheckBox();
  return <CheckBoxIcon isChecked={isChecked} disabled={disabled} />;
};

const Label = ({ children, className }: LabelProps) => {
  const { targetValue } = useCheckBox();

  return (
    <label htmlFor={targetValue} className={className}>
      {children}
    </label>
  );
};

CheckBox.Indicator = Indicator;
CheckBox.Label = Label;
CheckBox.Icon = Icon;

export default CheckBox;
